import React from "react";
import { useTranslation } from "react-i18next";
import { HiCog, HiExclamationTriangle } from "react-icons/hi2";
import { CustomButton } from "./CustomButton";
import { Helmet } from "react-helmet-async";

interface ErrorTemplateProps {
  errorType?: "temporary" | "critical";
  errorCode?: number;
  showTryAgain?: boolean;
  showHelmet?: boolean;
  onTryAgain?: () => void;
}

const ErrorTemplate: React.FC<ErrorTemplateProps> = ({
  errorType = "temporary",
  errorCode,
  showTryAgain = false,
  showHelmet = false,
  onTryAgain,
}) => {
  const { t } = useTranslation();
  const isTemporary = errorType === "temporary";

  return (
    <>
      {showHelmet && (
        <Helmet>
          <title>{`Super User Manager - ${t("serverError.title")}`}</title>
          <meta
            name="description"
            content="The easiest and most secure way to manage your users."
          />
          <meta name="robots" content="noindex" />
          <link
            rel="canonical"
            href={`${process.env.REACT_APP_HOST_URL}/control-panel`}
          />
        </Helmet>
      )}

      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="flex justify-center mb-6">
            {isTemporary ? (
              <div className="bg-amber-100 rounded-full p-4">
                <HiCog
                  className="w-16 h-16 text-amber-500 animate-spin"
                  style={{ animationDuration: "3s" }}
                />
              </div>
            ) : (
              <div className="bg-red-100 rounded-full p-4">
                <HiExclamationTriangle className="w-16 h-16 text-red-500" />
              </div>
            )}
          </div>

          <div className="space-y-4 mb-8">
            {isTemporary ? (
              <>
                <h1 className="text-2xl font-semibold text-gray-900">
                  {t("serverError.temporaryTitle")}
                </h1>
                <p className="text-gray-600 leading-relaxed">
                  {t("serverError.temporaryMessage")}
                </p>
                <p className="text-sm text-gray-500">
                  {t("serverError.reportSent")}
                </p>
              </>
            ) : (
              <>
                <h1 className="text-2xl font-semibold text-gray-900">
                  {t("serverError.criticalTitle")}
                </h1>
                <p className="text-gray-600 leading-relaxed">
                  {t("serverError.criticalMessage")}
                </p>
                <p className="text-sm text-gray-500">
                  {t("serverError.reportSent")}
                </p>
                {errorCode && (
                  <p className="text-xs text-gray-400">
                    {t("serverError.errorCode", { code: errorCode })}
                  </p>
                )}
              </>
            )}
          </div>

          <div className="space-y-3 mb-6">
            <a href="/control-panel">
              <CustomButton
                variant="primary"
                title={t("serverError.returnButton")}
                className="w-full py-3"
              />
            </a>

            {showTryAgain && !isTemporary && (
              <button
                onClick={onTryAgain || (() => window.location.reload())}
                className="w-full py-3 px-4 text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t("serverError.tryAgainButton")}
              </button>
            )}
          </div>

          <div className="text-sm text-gray-500 border-t pt-4">
            <p>
              {t("serverError.supportMessage")}{" "}
              <a
                href="mailto:info@super-user-manager.com"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                {t("serverError.supportLink")}
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default ErrorTemplate;
